import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { GuiButton } from "../../components/global-ui/gui-button";
import { GuiCheckbox } from "../../components/global-ui/gui-checkbox";
import { GuiTextbox } from "../../components/global-ui/gui-textbox";
import { ConfirmDeleteModal } from "../../components/confirm-delete-modal";
import LoadingSpinner from "../../components/loading-spinner";
import { showToast } from "../../components/toast";
import actions from "../../actions/actions";
import { MaxAlertCategories } from "../../utilities/constants";

function buildRows(accountAlertCategories) {
  const rows = [];

  for (let index = 1; index <= MaxAlertCategories; index += 1) {
    const label = (
      accountAlertCategories?.[`CategoryName${index}`] ??
      accountAlertCategories?.[`categoryName${index}`] ??
      accountAlertCategories?.[`optOutLabel${index}`] ??
      ""
    ).trim();

    if (!label) continue;

    rows.push({
      key: index,
      label,
      allowOptOut: Boolean(
        accountAlertCategories?.[`OptOutAllowed${index}`] ??
        accountAlertCategories?.[`optOutAllowed${index}`],
      ),
    });
  }

  return rows;
}

function buildPayload(rows) {
  const payload = {};

  for (let index = 1; index <= MaxAlertCategories; index += 1) {
    const row = rows[index - 1];
    const categoryName = row?.label?.trim() || "";

    payload[`CategoryName${index}`] = categoryName;
    payload[`OptOutAllowed${index}`] = categoryName
      ? Boolean(row?.allowOptOut)
      : null;
  }

  return payload;
}

export default function AccountAlertCategories({
  accountAlertCategories,
  onSave,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [rows, setRows] = useState([]);
  const [newCategory, setNewCategory] = useState({
    label: "",
    allowOptOut: false,
  });
  const [saving, setSaving] = useState(false);
  const [isRefreshingAfterSave, setIsRefreshingAfterSave] = useState(false);
  const [newCategoryError, setNewCategoryError] = useState("");
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState(null);
  const isDataLoading = accountAlertCategories === undefined;
  const showLoadingOverlay = saving || isRefreshingAfterSave;
  useEffect(() => {
    setRows(buildRows(accountAlertCategories));
    setNewCategory({ label: "", allowOptOut: false });
    setNewCategoryError("");
    setIsEditing(false);
    setIsRefreshingAfterSave(false);
    setPendingDeleteIndex(null);
  }, [accountAlertCategories]);

  function updateRow(index, changes) {
    setRows((currentRows) =>
      currentRows.map((row, rowIndex) =>
        rowIndex === index ? { ...row, ...changes } : row,
      ),
    );
  }

  function handleAddCategory() {
    const label = newCategory.label.trim();

    if (!label) {
      setNewCategoryError("Category name is required.");
      return;
    }

    if (rows.length >= MaxAlertCategories) {
      setNewCategoryError(
        `You can add up to ${MaxAlertCategories} categories.`,
      );
      return;
    }

    setRows((currentRows) => [
      ...currentRows,
      {
        key: currentRows.length + 1,
        label,
        allowOptOut: newCategory.allowOptOut,
      },
    ]);
    setNewCategory({ label: "", allowOptOut: false });
    setNewCategoryError("");
  }

  async function handleSave() {
    const normalizedRows = rows.map((row) => ({
      ...row,
      label: row.label.trim(),
    }));

    const pendingNewCategoryLabel = newCategory.label.trim();
    const rowsToSave = pendingNewCategoryLabel
      ? [
          ...normalizedRows,
          {
            key: normalizedRows.length + 1,
            label: pendingNewCategoryLabel,
            allowOptOut: newCategory.allowOptOut,
          },
        ]
      : normalizedRows;

    if (rowsToSave.length > MaxAlertCategories) {
      showToast("error", `You can add up to ${MaxAlertCategories} categories.`);
      return;
    }

    setSaving(true);

    try {
      const result = await actions.account.updateAccountAlertCategories(
        buildPayload(rowsToSave),
      );

      if (!result) {
        showToast("error", "Failed to save alert categories.");
        return;
      }

      showToast("success", "Alert categories saved.");
      setIsEditing(false);
      if (onSave) {
        setIsRefreshingAfterSave(true);
        onSave();
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteCategory(index) {
    if (index === 0 || saving) return;

    const rowsWithDeletedCategory = rows.map((row, rowIndex) =>
      rowIndex === index ? { ...row, label: "", allowOptOut: false } : row,
    );

    setSaving(true);

    try {
      const result = await actions.account.updateAccountAlertCategories(
        buildPayload(rowsWithDeletedCategory),
      );

      if (!result) {
        showToast("error", "Failed to delete alert category.");
        return;
      }

      setRows(
        rowsWithDeletedCategory.filter((row) => Boolean(row.label.trim())),
      );
      showToast("success", "Alert category deleted.");

      if (onSave) {
        setIsRefreshingAfterSave(true);
        onSave();
      }
    } finally {
      setSaving(false);
    }
  }

  async function confirmDeleteCategory() {
    if (pendingDeleteIndex === null) return;

    await handleDeleteCategory(pendingDeleteIndex);
    setPendingDeleteIndex(null);
  }

  function handleEditClick() {
    setNewCategoryError("");
    setIsEditing(true);
  }

  function handleCancel() {
    setRows(buildRows(accountAlertCategories));
    setNewCategory({ label: "", allowOptOut: false });
    setNewCategoryError("");
    setPendingDeleteIndex(null);
    setIsEditing(false);
  }

  const canAddMore = rows.length < MaxAlertCategories;
  const showAddButton = rows.length < MaxAlertCategories - 1;

  if (isDataLoading) {
    return <LoadingSpinner show={true} className="loading-page" />;
  }

  return (
    <>
      <div className="acct-alert-categories">
        <div className="acct-alert-categories__header">
          <div className="acct-alert-categories__title">Alert Categories</div>
          {!isEditing && (
            <GuiButton
              className="btn-outline acct-alert-categories__edit-btn"
              onClick={handleEditClick}
            >
              <i className="fas fa-pen" /> Edit
            </GuiButton>
          )}
        </div>

        <div className="acct-alert-categories__card">
          <div className="acct-alert-categories__row acct-alert-categories__row--header">
            <div className="acct-alert-categories__cell acct-alert-categories__cell--name">
              Category Name
            </div>
            <div className="acct-alert-categories__cell acct-alert-categories__cell--opt-out">
              Allow Opt-Out
            </div>
          </div>

          {rows.map((row, index) => (
            <div className="acct-alert-categories__row" key={row.key}>
              <div className="acct-alert-categories__cell acct-alert-categories__cell--name">
                {isEditing ? (
                  <GuiTextbox
                    className="acct-alert-categories__textbox"
                    name={`categoryName${index + 1}`}
                    value={row.label}
                    disabled={index === 0}
                    onChange={(_name, value) =>
                      updateRow(index, { label: value })
                    }
                    maxLength={50}
                  />
                ) : (
                  <span className="acct-alert-categories__value">
                    {row.label}
                  </span>
                )}
              </div>
              <div className="acct-alert-categories__cell acct-alert-categories__cell--opt-out acct-alert-categories__cell--checkbox">
                {isEditing ? (
                  <div className="acct-alert-categories__checkbox-actions">
                    <GuiCheckbox
                      name={`optOutAllowed${index + 1}`}
                      value={row.allowOptOut}
                      onChange={(_, value) =>
                        updateRow(index, { allowOptOut: value })
                      }
                    />
                    {index !== 0 && (
                      <button
                        type="button"
                        className="acct-alert-categories__delete-btn"
                        onClick={() => setPendingDeleteIndex(index)}
                        disabled={saving}
                        aria-label={`Delete ${row.label || "category"}`}
                        title="Delete category"
                      >
                        <i className="far fa-trash-alt" />
                      </button>
                    )}
                  </div>
                ) : (
                  <span
                    className={`acct-alert-categories__badge${row.allowOptOut ? " acct-alert-categories__badge--allowed" : " acct-alert-categories__badge--not-allowed"}`}
                  >
                    {row.allowOptOut ? "Allowed" : "Not Allowed"}
                  </span>
                )}
              </div>
            </div>
          ))}

          {isEditing && canAddMore && (
            <>
              <div className="acct-alert-categories__row">
                <div className="acct-alert-categories__cell acct-alert-categories__cell--name">
                  <div className="acct-alert-categories__input-group">
                    <GuiTextbox
                      className={`acct-alert-categories__textbox${newCategoryError ? " acct-alert-categories__textbox--error" : ""}`}
                      name="newCategoryName"
                      value={newCategory.label}
                      onChange={(_name, value) => {
                        setNewCategory((current) => ({
                          ...current,
                          label: value,
                        }));
                        setNewCategoryError("");
                      }}
                      maxLength={50}
                    />
                    {newCategoryError && (
                      <div className="acct-alert-categories__error">
                        {newCategoryError}
                      </div>
                    )}
                  </div>
                </div>
                <div className="acct-alert-categories__cell acct-alert-categories__cell--opt-out acct-alert-categories__cell--checkbox">
                  <GuiCheckbox
                    name="newOptOutAllowed"
                    value={newCategory.allowOptOut}
                    onChange={(_, value) =>
                      setNewCategory((current) => ({
                        ...current,
                        allowOptOut: value,
                      }))
                    }
                  />
                </div>
              </div>

              {showAddButton && (
                <button
                  type="button"
                  className="acct-alert-categories__add-row"
                  onClick={handleAddCategory}
                >
                  <i className="far fa-plus" /> Add Category
                </button>
              )}
            </>
          )}
        </div>

        {isEditing && (
          <div className="acct-alert-categories__actions">
            <GuiButton
              className="btn-outline acct-alert-categories__cancel-btn"
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </GuiButton>
            <GuiButton
              className="acct-alert-categories__save-btn"
              onClick={handleSave}
              loading={saving}
            >
              <i className="fas fa-save" /> {saving ? "Saving..." : "Save"}
            </GuiButton>
          </div>
        )}
      </div>
      {pendingDeleteIndex !== null && (
        <ConfirmDeleteModal
          status="remove"
          title="Delete category?"
          message="This action cannot be undone."
          confirmText="Delete"
          deleting={saving}
          onDelete={confirmDeleteCategory}
          onCancel={() => setPendingDeleteIndex(null)}
        />
      )}
      <LoadingSpinner show={showLoadingOverlay} className="loading-page" />
    </>
  );
}
